import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { useToast } from '../ui';

import { Dialog, DialogHeader, DialogBody } from '../ui/Dialog';
import { settingsAPI } from '../../services/api';
import useTelemetry from '../../hooks/useTelemetry';
import useSettingsList from '../../hooks/useSettingsList';
import TypeaheadChips from '../ui/TypeaheadChips';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../ui/PageHeader';



export default function TaxRatesPage() {
  const [taxRates, setTaxRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const { emit } = useTelemetry();
  const toast = useToast();

  const fetchRates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.getTaxRates();
      setTaxRates(res?.rates || []);
    } catch (err) {
      setError('Failed to load tax rates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const handleAdd = () => {
    setEditingRate(null);
    setModalOpen(true);
  };

  const handleEdit = (rate) => {
    setEditingRate(rate);
    setModalOpen(true);
  };

  const handleSave = async (formData) => {
    // IDI-668: one rate per state. Blank ("All") rows and duplicates that
    // already exist are left alone; only this save is refused.
    const st = String(formData.state || '').trim().toUpperCase();
    if (st && taxRates.some((r) => r.id !== editingRate?.id && String(r.state || '').trim().toUpperCase() === st)) {
      throw new Error(`${st} already has a tax rate.`);
    }
    try {
      const updatedRates = editingRate
        ? taxRates.map(r => r.id === editingRate.id ? { ...r, ...formData } : r)
        : [...taxRates, { ...formData, id: `tax_${Date.now()}` }];

      await settingsAPI.updateTaxRates({ rates: updatedRates });
      setTaxRates(updatedRates);
      toast.success(editingRate ? 'Tax rate updated' : 'Tax rate added');
      setModalOpen(false);
      setEditingRate(null);
      emit('settings.tax_updated', { action: editingRate ? 'update' : 'add' });
    } catch (err) {
      throw new Error('Failed to save tax rate');
    }
  };

  const handleDelete = async (rate) => {
    try {
      const updatedRates = taxRates.filter(r => r.id !== rate.id);
      await settingsAPI.updateTaxRates({ rates: updatedRates });
      setTaxRates(updatedRates);
      toast.success('Tax rate deleted');
      emit('settings.tax_updated', { action: 'delete' });
    } catch (err) {
      setError('Failed to delete tax rate');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="space-y-3"><div className="h-8 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /><div className="h-32 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /></div>
      </div>
    );
  }

  return (
    <div className="px-6">
      <PageHeader title="Tax & Rates" subtitle="One rate per state." actions={<Button onClick={handleAdd}><PlusIcon className="h-4 w-4 mr-1.5" />Add</Button>} />

      {error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="overflow-hidden">
        {taxRates.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-500">No tax rates configured.</p>
            <Button onClick={handleAdd} variant="secondary" size="sm" className="mt-4">
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Add First Tax Rate
            </Button>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>State</th>
                <th style={{ textAlign: 'right' }}>Rate</th>
                <th style={{ textAlign: 'center' }}>Exempt</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {taxRates.map(rate => (
                <tr key={rate.id} className="group">
                  <td className="font-medium">
                    {rate.name}
                    {rate.isDefault && (
                      <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)' }}>Default</span>
                    )}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)' }}>{rate.state || 'All'}</td>
                  <td style={{ textAlign: 'right' }}>{rate.rate}%</td>
                  <td style={{ textAlign: 'center' }}>
                    {rate.exempt_types?.length > 0 && (
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{rate.exempt_types.join(', ')}</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!rate.isDefault && (
                        <button
                          onClick={async () => {
                            const updated = taxRates.map(r => ({ ...r, isDefault: r.id === rate.id }));
                            await settingsAPI.updateTaxRates({ rates: updated });
                            setTaxRates(updated);
                            toast.success(`${rate.name} set as default`);
                          }}
                          className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 text-xs"
                          aria-label={`Set ${rate.name} as default`}
                          title="Set as default"
                        >
                          Default
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(rate)}
                        className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        aria-label={`Edit ${rate.name}`}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(rate)}
                        className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                        aria-label={`Delete ${rate.name}`}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>



      {/* Add/Edit Modal */}
      <TaxRateModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingRate(null); }}
        rate={editingRate}
        onSave={handleSave}
      />
    </div>
  );
}

/** GP-21: dropdown sourced from Settings → Customer Types. */
function ExemptTypesDropdown({ value = [], onChange }) {
  const { items: customerTypes } = useSettingsList('customer_types');
  const options = customerTypes.map(ct => ({ value: ct.name || ct.label, label: ct.name || ct.label }));
  return (
    <TypeaheadChips
      value={value}
      onChange={onChange}
      options={options}
      placeholder="Select exempt customer types…"
      emptyMessage="No customer types configured"
    />
  );
}

function TaxRateModal({ open, onClose, rate, onSave }) {
  const [form, setForm] = useState({ name: '', state: '', rate: '', exempt_types: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({
        name: rate?.name || '',
        state: rate?.state || '',
        rate: rate?.rate || '',
        exempt_types: rate?.exempt_types || [],
      });
      setError(null);
    }
  }, [open, rate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.rate) {
      setError('Name and rate are required');
      return;
    }
    try {
      setSaving(true);
      await onSave({
        ...form,
        rate: parseFloat(form.rate),
        exempt_types: form.exempt_types || [],
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader onClose={onClose}>{rate ? 'Edit Tax Rate' : 'Add Tax Rate'}</DialogHeader>
      <DialogBody>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <Alert variant="error">{error}</Alert>}

          <div>
            <label htmlFor="tax-name" className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <Input
              id="tax-name"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., State Sales Tax"
              required
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="tax-state" className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <Input
                id="tax-state"
                value={form.state}
onChange={(e) => setForm(prev => ({ ...prev, state: e.target.value.toUpperCase() }))}
                placeholder="e.g., CA"
                maxLength={2}
              />
            </div>
            <div>
              <label htmlFor="tax-rate" className="block text-sm font-medium text-gray-700 mb-1">
                Rate % <span className="text-red-500">*</span>
              </label>
              <Input
                id="tax-rate"
                type="number"
                min="0"
                max="100"
                step="0.001"
                value={form.rate}
                onChange={(e) => setForm(prev => ({ ...prev, rate: e.target.value }))}
                placeholder="e.g., 8.25"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="tax-exempt" className="block text-sm font-medium text-gray-700 mb-1">
              Exempt Customer Types
            </label>
            <ExemptTypesDropdown
              value={form.exempt_types}
              onChange={(val) => setForm(prev => ({ ...prev, exempt_types: val }))}
            />
            <p className="text-xs text-gray-500 mt-1">Leave blank if no exemptions apply.</p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[color:var(--color-border)]">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </form>
      </DialogBody>
    </Dialog>
  );
}
